'use client';

import { cn } from '@core/utils';
import { createContext, useId } from 'react';

import type { HTMLTagProps } from '../../../types';
import styles from './InputDecorator.module.scss';

interface InputDecoratorProps extends HTMLTagProps<'div'> {
  label?: React.ReactNode;
  required?: boolean;
  validationMessage?: string;
  message?: string;
  htmlFor?: string;
  tooltip?: React.ReactNode;
  labelClassName?: string;
  messageClassName?: string;
}

type InputDecoratorContextValue = Pick<
  InputDecoratorProps,
  'label' | 'validationMessage'
> & {
  /**
   * 메시지 영역의 id. 메시지가 없으면 undefined다.
   * 소비 컴포넌트가 `aria-describedby`에 그대로 넘기면 입력과 메시지가 연결된다.
   */
  messageId?: string;
};

export const InputDecoratorContext =
  createContext<InputDecoratorContextValue | null>(null);

export const InputDecorator = ({
  label,
  required,
  validationMessage,
  message,
  className,
  labelClassName,
  messageClassName,
  children,
  htmlFor,
  tooltip,
  ...restProps
}: InputDecoratorProps) => {
  const hasMessage = !!(validationMessage || message);
  const messageId = `${useId()}-message`;

  return (
    <div {...restProps} className={cn('flex flex-col', className)}>
      {label || tooltip ? (
        <div className="mb-8 flex items-center gap-4">
          {label ? (
            <label
              className={cn(
                'w-full text-start font-400 text-Gray-800 typo-body4',
                {
                  "after:content-['*'] after:text-R-500 after:ml-1": !!required,
                },
                labelClassName,
              )}
              htmlFor={
                htmlFor ?? (typeof label === 'string' ? label : undefined)
              }
            >
              {label}
            </label>
          ) : null}
          {tooltip}
        </div>
      ) : null}
      <InputDecoratorContext.Provider
        value={{
          label,
          validationMessage,
          messageId: hasMessage ? messageId : undefined,
        }}
      >
        {children}
      </InputDecoratorContext.Provider>
      {hasMessage ? (
        <div
          id={messageId}
          className="mx-5 mt-4 flex flex-col gap-8 font-400 typo-caption1"
        >
          {validationMessage ? (
            //* 검증 실패는 입력 뒤에 나타나는 동적 콘텐츠라 스크린리더가 알아채지 못한다.
            <span role="alert" className={styles['validation-message']}>
              {validationMessage}
            </span>
          ) : null}

          {message ? (
            <span className={cn('text-Gray-600', messageClassName)}>
              {message}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
